import { useDebouncedValue } from '@tanstack/react-pacer'
import type { UseQueryResult } from '@tanstack/react-query'
import { useState } from 'react'
import { getPickerErrorMessage } from '../../utils/error-handling/utils.ts'
import { SEARCH_DEBOUNCE_WAIT_MS, useIsStalePlaceholderData } from '../../utils/query-utils.ts'
import { SearchableCombobox, type SearchableComboboxProps } from './searchable-combobox.tsx'

export type SearchPickerOption = {
  value: string
  label: string
  enabled?: boolean
  /** Marks an item chosen before, such as a project already connected. The current pick is always checked. */
  checked?: boolean
  metadata?: string
}

type FieldState<TItem> = { kind: 'picked'; item: TItem } | { kind: 'searching'; term: string }

export type SearchPickerProps<TItem, TInput> = {
  label: string
  placeholder: string
  isDisabled?: boolean
  /** What the picker lists, for its messages: "Choose a project from the list.", "No projects found." */
  noun: { singular: string; plural: string }
  /** What the listed items belong to, read as "in this <container>" by the nudge an empty list shows. */
  container?: string
  /** What the list says when a typed search matches nothing, for a picker that can say more. */
  emptyMessage?: string
  initialValue?: TItem
  onChange: (item: TItem | undefined) => unknown
  /**
   * The lookup behind the list, called with `searchInput` and the debounced term. Like any hook it
   * has to be the same one for the picker's lifetime.
   */
  useSearch: (input: TInput & { search: string }) => UseQueryResult<{ _embedded: { items: TItem[] } }>
  /**
   * Which listing to search. Key the picker on every field of it: the previous answer stands in
   * while the next loads, so a picker kept across a change of connection or container would offer,
   * and let the user pick, the previous one's items.
   */
  searchInput: NoInfer<TInput>
  /** Labels each item by what `search` matches, so editing the field's text searches for it. */
  toOption: (item: TItem) => SearchPickerOption
} & Pick<SearchableComboboxProps, 'side' | 'avoidCollisions'>

/**
 * A typeahead over one page of what matches the typed text. The text is the search until an option
 * is picked; then it shows the pick's label, and editing it drops the pick and searches again.
 */
export const SearchPicker = <TItem, TInput>({
  label,
  placeholder,
  isDisabled,
  noun,
  container,
  emptyMessage = `No ${noun.plural} found.`,
  initialValue,
  onChange,
  useSearch,
  searchInput,
  toOption,
  side,
  avoidCollisions,
}: SearchPickerProps<TItem, TInput>) => {
  const [field, setField] = useState<FieldState<TItem>>(() =>
    initialValue === undefined ? { kind: 'searching', term: '' } : { kind: 'picked', item: initialValue }
  )
  const [debouncedTerm] = useDebouncedValue(field.kind === 'searching' ? field.term : '', {
    wait: SEARCH_DEBOUNCE_WAIT_MS,
  })

  const {
    data,
    isLoading,
    isPlaceholderData: isShowingPreviousPage,
    error,
  } = useSearch({
    ...searchInput,
    search: debouncedTerm,
  })
  // Not the query's own `isFetching`: a background refetch keeps a list that is still right, while
  // a previous term's page that has stood in for a moment is not this term's answer.
  const isShowingStalePage = useIsStalePlaceholderData(isShowingPreviousPage)
  // The combobox stops nudging as soon as anything is typed. This covers the other window: once the
  // field is cleared, the previous search's answer stays on screen until the debounce settles and
  // the unsearched answer arrives, and only that answer can say the account sees nothing.
  const isShowingUnsearchedAnswer = debouncedTerm === '' && !isShowingPreviousPage
  const items = data?._embedded.items ?? []
  const pickedValue = field.kind === 'picked' ? toOption(field.item).value : ''

  const handleSelect = (optionValue: string) => {
    const picked = items.find(item => toOption(item).value === optionValue)
    if (picked === undefined) {
      return
    }
    setField({ kind: 'picked', item: picked })
    onChange(picked)
  }

  const handleSearchChange = (term: string) => {
    setField({ kind: 'searching', term })
    onChange(undefined)
  }

  const errorText = getPickerErrorMessage(error)

  return (
    <SearchableCombobox
      label={label}
      placeholder={placeholder}
      searchValue={field.kind === 'picked' ? toOption(field.item).label : field.term}
      setSearchValue={handleSearchChange}
      selection={{ value: pickedValue, onSelect: handleSelect }}
      isFetching={isLoading || isShowingStalePage}
      isDisabled={isDisabled}
      errorText={errorText}
      helperText={
        field.kind === 'searching' && field.term !== '' ? `Choose a ${noun.singular} from the list.` : undefined
      }
      emptyMessage={errorText === undefined ? emptyMessage : `Could not load ${noun.plural}.`}
      emptyListNudge={isShowingUnsearchedAnswer ? { noun: noun.plural, container } : undefined}
      options={items.map(item => {
        const option = toOption(item)
        return { ...option, checked: option.checked === true || option.value === pickedValue }
      })}
      side={side}
      avoidCollisions={avoidCollisions}
    />
  )
}
